import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { StoreService } from '../services/StoreService';
import { IStore, ICategory, IProduct, IVariableDescription } from '../types/Store';
import { Environment } from '../environment';

interface IStoreContextData {
    store: IStore | null;
    categories: ICategory[];
    products: IProduct[];
    allProducts: IProduct[];
    variableDescriptions: IVariableDescription[];
    deliveryTimes: { tempoMedioEntrega: number | null; tempoMedioRetirada: number | null };
    sauces: any[];
    sauceLists: any[];
    sideDishes: any[];
    sideDishLists: any[];
    additionals: any[];
    additionalLists: any[];
    flavors: any[];
    flavorLists: any[];
    flavorClassifications: any[];
    salads: any[];
    saladLists: any[];
    desserts: any[];
    dessertLists: any[];
    ingredients: any[];
    ingredientLists: any[];
    isLoading: boolean;
    error: string | null;
    refreshStatus: () => Promise<any>;
}

const StoreContext = createContext<IStoreContextData>({} as IStoreContextData);

export const useStore = () => useContext(StoreContext);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [store, setStore] = useState<IStore | null>(null);
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [products, setProducts] = useState<IProduct[]>([]);
    const [allProducts, setAllProducts] = useState<IProduct[]>([]);
    const [variableDescriptions, setVariableDescriptions] = useState<IVariableDescription[]>([]);
    const [sauces, setSauces] = useState<any[]>([]);
    const [sauceLists, setSauceLists] = useState<any[]>([]);
    const [sideDishes, setSideDishes] = useState<any[]>([]);
    const [sideDishLists, setSideDishLists] = useState<any[]>([]);
    const [additionals, setAdditionals] = useState<any[]>([]);
    const [additionalLists, setAdditionalLists] = useState<any[]>([]);
    const [flavors, setFlavors] = useState<any[]>([]);
    const [flavorLists, setFlavorLists] = useState<any[]>([]);
    const [flavorClassifications, setFlavorClassifications] = useState<any[]>([]);
    const [salads, setSalads] = useState<any[]>([]);
    const [saladLists, setSaladLists] = useState<any[]>([]);
    const [desserts, setDesserts] = useState<any[]>([]);
    const [dessertLists, setDessertLists] = useState<any[]>([]);
    const [ingredients, setIngredients] = useState<any[]>([]);
    const [ingredientLists, setIngredientLists] = useState<any[]>([]);
    const [deliveryTimes, setDeliveryTimes] = useState<{ tempoMedioEntrega: number | null; tempoMedioRetirada: number | null }>({ tempoMedioEntrega: null, tempoMedioRetirada: null });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const getSubdomain = () => {
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('store') || 'chickenhouse';
        }
        if (hostname.endsWith(Environment.DOMAIN)) {
            const subdomain = hostname.replace(`.${Environment.DOMAIN}`, '');
            if (subdomain !== Environment.DOMAIN && subdomain !== hostname) return subdomain;
        }
        return parts.length > 2 ? parts[0] : null;
    };

    const loadStoreData = useCallback(async () => {
        try {
            setIsLoading(true);
            const slug = getSubdomain();

            if (!slug) {
                setError('Loja não identificada');
                setIsLoading(false);
                return;
            }

            const storeData = await StoreService.getBySlug(slug);
            
            try {
                const profileStore = await StoreService.getProfileStore(storeData.id);
                if (profileStore) {
                    storeData.profile = profileStore;
                    
                    if (profileStore.headerColor) {
                        storeData.headerColor = profileStore.headerColor;
                    }

                    if (profileStore.headerLogoSize) {
                        storeData.headerLogoSize = profileStore.headerLogoSize;
                    }

                    if (profileStore.bussinesLogoPictureId) {
                        storeData.logo = profileStore.bussinesLogoPictureId;
                    }

                    if (profileStore.adressId) {
                        const addressData = await StoreService.getAddress(profileStore.adressId);
                        if (addressData) {
                            storeData.address = `${addressData.address}, ${addressData.city} - ${addressData.uf}, CEP: ${addressData.cep}`;
                        }
                    }

                    setDeliveryTimes({
                        tempoMedioEntrega: (profileStore as any).tempoMedioEntrega ?? null,
                        tempoMedioRetirada: (profileStore as any).tempoMedioRetirada ?? null,
                    });
                }
            } catch (_) {}

            setStore(storeData);

            const [
                categoriesData, productsData,
                saucesData, sauceListsData,
                sideDishesData, sideDishListsData,
                additionalsData, additionalListsData,
                flavorsData, flavorListsData, flavorClassificationsData,
                saladsData, saladListsData,
                dessertsData, dessertListsData,
                ingredientsData, ingredientListsData,
                variableDescriptionsData,
            ] = await Promise.all([
                StoreService.getCategories(storeData.id),
                StoreService.getProducts(storeData.id),
                StoreService.getSauces(storeData.id),
                StoreService.getSauceLists(storeData.id),
                StoreService.getSideDishes(storeData.id),
                StoreService.getSideDishLists(storeData.id),
                StoreService.getAdditionals(storeData.id),
                StoreService.getAdditionalLists(storeData.id),
                StoreService.getFlavors(storeData.id),
                StoreService.getFlavorLists(storeData.id),
                StoreService.getFlavorClassifications(storeData.id),
                StoreService.getSalads(storeData.id),
                StoreService.getSaladLists(storeData.id),
                StoreService.getDesserts(storeData.id),
                StoreService.getDessertLists(storeData.id),
                StoreService.getIngredients(storeData.id),
                StoreService.getIngredientLists(storeData.id),
                StoreService.getVariableDescriptions(storeData.id),
            ]);

            setCategories(categoriesData);
            setAllProducts(productsData);
            setProducts(productsData.filter((p: any) => !p.produtoInterno));
            setVariableDescriptions(variableDescriptionsData);
            setSauces(saucesData);
            setSauceLists(sauceListsData);
            setSideDishes(sideDishesData);
            setSideDishLists(sideDishListsData);
            setAdditionals(additionalsData);
            setAdditionalLists(additionalListsData);
            setFlavors(flavorsData);
            setFlavorLists(flavorListsData);
            setFlavorClassifications(flavorClassificationsData);
            setSalads(saladsData);
            setSaladLists(saladListsData);
            setDesserts(dessertsData);
            setDessertLists(dessertListsData);
            setIngredients(ingredientsData);
            setIngredientLists(ingredientListsData);
            setError(null);

            import('../services/TrackingService').then(({ TrackingService }) => {
                TrackingService.trackVisit(storeData.id);
            });

        } catch (_) {
            setError('Loja não encontrada ou erro na conexão');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const refreshStatus = useCallback(async () => {
        if (!store) return null;
        return await StoreService.getStatus(store.id);
    }, [store]);

    useEffect(() => {
        loadStoreData();
    }, [loadStoreData]);

    return (
        <StoreContext.Provider value={{
            store, categories, products, allProducts,
            variableDescriptions, deliveryTimes,
            sauces, sauceLists, 
            sideDishes, sideDishLists, 
            additionals, additionalLists, 
            flavors, flavorLists, flavorClassifications,
            salads, saladLists,
            desserts, dessertLists,
            ingredients, ingredientLists,
            isLoading, error, refreshStatus 
        }}>
            {children}
        </StoreContext.Provider>
    );
};
